import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Stack,
  Card,
  Alert,
  Snackbar,
  CardContent,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { getAllUsers, getClientUsers, manageUser, getUserSubscriptionPlan } from "../../api/apiService";

export default function Users() {
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");

  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [userMetrics, setUserMetrics] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editAction, setEditAction] = useState("activate");
  const [selectedId, setSelectedId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [deleteName, setDeleteName] = useState("");
  const [viewName, setViewName] = useState("");

  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriberName, setSubscriberName] = useState("");

  const totalPages = Math.ceil(rowCount / pageSize) || 0;
  const from = rowCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, rowCount);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllUsers(page, pageSize);
      const data = res?.data?.data || [];
      setRows(data.map(u => ({ ...u })));
      setRowCount(res?.data?.total || 0);
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to load users", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  const fetchUserMetrics = async () => {
    try {
      const res = await getClientUsers(0, 0);
      const metrics = res?.data?.data ?? [];
      setUserMetrics(metrics);

    } catch (e) {
      setSnackbar({ open: true, message: "Failed to load user metrics", severity: "error" });
    }
  };


  useEffect(() => {
    fetchUserMetrics();
  }, [])

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const columns = [
    { field: "fullName", headerName: "Full Name", width: 200,
      valueGetter: (value, row) => `${row.firstName || ''} ${row.lastName || ''}`.trim(),
      renderCell: (params) => {
        if (params.row.role === "ADMIN") {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography>
                {params?.value || '—'}
              </Typography>
            </Box>
          );   // hide for admins
        }

        return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography
            onClick={() => {
              const found = userMetrics.find(u => u.email === params.row.email);
              setViewName(params.row.firstName + " " + params.row.lastName)
              setViewData(found);
              setViewOpen(true);
            }}
            sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {params?.value || '—'}
          </Typography>
        </Box>
        )
      }
    },
    { field: "email", headerName: "Email", width: 340,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', overflowWrap: 'anywhere' }}>
          {params?.value || '—'}
        </Box>
      )
    },
    { field: "role", headerName: "Role", width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', py: 1, height: '100%', alignItems: "center", }}>
          <Chip
            label={params?.value || '—'}
            color={String(params.value).toLowerCase() === 'admin' ? 'primary' : 'default'}
            variant="filled"
            size="small"
          />
        </Box>
      )
    },
    { field: "status", headerName: "Status", width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', py: 1, height: '100%', alignItems: "center", }}>
          <Chip
            label={params?.value || '—'}
            color={params.value === 'active' ? 'success' : 'default'}
            variant="filled"
            size="small"
          />
        </Box>
      )
    },
    { field: "createdAt", headerName: "Created", width: 180,
      renderCell: (p) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{p?.value ? new Date(p.value).toLocaleString() : '—'}</Box>
    },
    { field: "lastLoginAt", headerName: "Last Login", width: 180,
      renderCell: (p) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{p?.value ? new Date(p.value).toLocaleString() : '—'}</Box>
    },
    { field: "actions", headerName: "Actions", width: 140,
      renderCell: (params) => (
       params.row.role !== "ADMIN" &&
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit User Account">
          <IconButton color="primary" onClick={() => { setSelectedId(params.row.id); setEditAction('activate'); setEditOpen(true); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          </Tooltip>
          <Tooltip title="User Subscription details">
          <IconButton color="primary" onClick={() => handleViewSubscription(params.row.id, params.row.firstName + " " + params.row.lastName)}>
            <SubscriptionsIcon fontSize="small" />
          </IconButton>
          </Tooltip>
          <Tooltip title="Delete User">
          <IconButton color="red" onClick={() => { setSelectedId(params.row.id); setDeleteName(params.row.firstName + " " + params.row.lastName); setConfirmOpen(true); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
          </Tooltip>
        </Stack>
      )
    },
  ];

  const handleManage = async (action) => {
    if (!selectedId) return;
    try {
      await manageUser({ userId: selectedId, action });
      setSnackbar({ open: true, message: 'Action successful', severity: 'success' });
      setEditOpen(false);
      setConfirmOpen(false);
      fetchUsers();
    } catch (e) {
      setSnackbar({ open: true, message: 'Action failed', severity: 'error' });
    }
  };

  const handleViewSubscription = async (userId, fullName) => {
    setSubscriptionLoading(true);
    setSubscriptionOpen(true);
    try {
      const res = await getUserSubscriptionPlan(userId);
      setSubscriptionData(res.data.data);
      setSubscriberName(fullName);
    } catch (error) {
      console.error("Failed to fetch subscription details:", error);
      setSnackbar({ open: true, message: "Failed to load subscription details", severity: "error" });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => [r.firstName, r.lastName, r.email, r.role, r.status].some(v => (v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <Box sx={{ mb: 2 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Grid sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Users</Typography>
        <TextField
          size="small"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
      </Grid>

      <Box sx={{height:510, width:'100%'}}>
        <Card elevation={0} sx={{borderRadius:2}}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {rowCount === 0 ? 'No records' : `Showing ${from}–${to} of ${rowCount} records`}
              </Typography>
              <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" shape="rounded" size="small" />
            </Stack>

            <DataGrid
              sx={{height:400,borderRadius:2, p:1}}
              rows={filteredRows}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              rowCount={rowCount}
              paginationMode="server"
              hideFooterPagination
              getRowHeight={() => "auto"}
              disableRowSelectionOnClick
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {rowCount === 0 ? 'No records' : `Showing ${from}–${to} of ${rowCount} records`}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="page-size-label">Rows per page</InputLabel>
                <Select labelId="page-size-label" value={pageSize} label="Rows per page" onChange={(e) => { setPageSize(e.target.value); setPage(1); }}>
                  {[10, 20, 50].map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" shape="rounded" size="small" />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 2 }}}}>
        <DialogTitle>{viewName} details</DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Box>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  {[//{ label: "Full Name", value: `${viewData.firstName || ''} ${viewData.lastName || ''}`.trim() },
                    { label: "Email", value: viewData?.email || '—'},
                    { label: "Status", value: viewData?.status || '—'},
                    { label: "Campaign Count", value: viewData?.campaignCount || 0 },
                    { label: "Lead Count", value: viewData?.leadCount || 0  },
                    { label: "Created At", value: viewData?.createdAt ? new Date(viewData?.createdAt).toLocaleString() : '—' },
                  ].map((item, index) => (
                    <Grid size={{xs:12, sm:12}} key={index}>
                      <Card variant='outlined' sx={{p:1}}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>{item.label}</Typography>
                        <Typography variant="body2">{item.value }</Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: 2 }}}}>
        <DialogTitle>Manage User Account</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="action-label">Action</InputLabel>
            <Select labelId="action-label" value={editAction} label="Action" onChange={(e) => setEditAction(e.target.value)}>
              <MenuItem value="activate">Activate</MenuItem>
              <MenuItem value="deactivate">Deactivate</MenuItem>
              {/* <MenuItem value="suspend">Suspend</MenuItem> */}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => handleManage(editAction)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: 2 }}}}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete  
             <strong> {deleteName || ""} </strong> 
            from the users list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleManage('delete')}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={subscriptionOpen} onClose={() => setSubscriptionOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
        <DialogTitle>Subscription Details {"of " + subscriberName || ""}</DialogTitle>
        <DialogContent dividers>
          {subscriptionLoading ? (
            <Typography>Loading subscription details...</Typography>
          ) : subscriptionData ? (
            <Box>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Plan</Typography>
                    <Typography variant="body1">{subscriptionData.plan || "—"}</Typography>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip
                      label={subscriptionData.status || "—"}
                      color={subscriptionData.status === 'active' ? 'success' : 'default'}
                      variant="filled"
                      size="small"
                    />
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Monthly Price</Typography>
                    <Typography variant="body1">{`${subscriptionData.monthlyPrice} ${subscriptionData.currency}` || "—"}</Typography>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Billing Cycle Start</Typography>
                    <Typography variant="body1">{subscriptionData.billingCycleStart ? new Date(subscriptionData.billingCycleStart).toLocaleString() : "—"}</Typography>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Billing Cycle End</Typography>
                    <Typography variant="body1">{subscriptionData.billingCycleEnd ? new Date(subscriptionData.billingCycleEnd).toLocaleString() : "—"}</Typography>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Next Billing Date</Typography>
                    <Typography variant="body1">{subscriptionData.nextBillingDate ? new Date(subscriptionData.nextBillingDate).toLocaleString() : "—"}</Typography>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Auto Renew</Typography>
                    <Typography variant="body1">{subscriptionData.autoRenew ? "Yes" : "No"}</Typography>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Trial Ends At</Typography>
                    <Typography variant="body1">{subscriptionData.trialEndsAt ? new Date(subscriptionData.trialEndsAt).toLocaleString() : "—"}</Typography>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Remaining Trial Days</Typography>
                    <Typography variant="body1">{subscriptionData.remainingTrialDays || 0}</Typography>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Active</Typography>
                    <Chip
                      label={subscriptionData.isActive ? "Yes" : "No"}
                      color={subscriptionData.isActive ? 'success' : 'default'}
                      variant="filled"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Card>

              <Stack sx={{ mt: 3 }} spacing={1}>
                <Typography variant="h6">Usage Limits</Typography>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    {subscriptionData.usageLimits && Object.entries(subscriptionData.usageLimits).map(([key, value], index) => (
                      <Grid size={{xs:12, sm:6}} key={index}>
                        <Typography variant="subtitle2" color="text.secondary">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</Typography>
                        <Typography variant="body1">{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Stack>

              <Stack sx={{ mt: 3 }} spacing={1}>
                <Typography variant="h6">Current Usage</Typography>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    {subscriptionData.currentUsage && Object.entries(subscriptionData.currentUsage).map(([key, value], index) => (
                      <Grid size={{xs:12, sm:6}} key={index}>
                        <Typography variant="subtitle2" color="text.secondary">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</Typography>
                        <Typography variant="body1">{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Stack>

              <Stack sx={{ mt: 3 }} spacing={1}>
                <Typography variant="h6">Plan Features</Typography>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    {subscriptionData.planFeatures && Object.entries(subscriptionData.planFeatures).map(([key, value], index) => (
                      <Grid size={{xs:12, sm:6}} key={index}>
                        <Typography variant="subtitle2" color="text.secondary">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</Typography>
                        <Typography variant="body1">{value ? "Yes" : "No"}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Stack>
            </Box>
          ) : (
            <Typography>No subscription details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
